import React, { useCallback, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { SelectValue } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import {
	fetchStaticGroupById,
	getRegionsProfile,
} from '../service/MySrcmService';
import { Region, UserRole } from '../shared/VolunteerApplicationServiceTypes';
import useUserSession from '../hooks/useUserSession';
import { getRegionsOfExaminationCandidates } from '../service/ExamModuleService';
import { useCallApi } from '../hooks/useCallApi';
import { Zone } from '../shared/ExamModuleTypes';
import { displayError } from '../shared/Utils';
import { useToastMessage } from '../hooks/useToastMessage';

const { Option } = Select;

interface OptionData {
	value: number | undefined;
	label: string | undefined;
}

interface RemoteSingleSelectProps {
	onChange?: (value: OptionData) => void;
	value?: OptionData | null | undefined;
	functionarySrcmId?: string;
	multiple?: boolean;
	getPreworkRegions?: boolean;
	batchId?: number;
	getPreceptorRegions?: boolean;
	preceptorBatchId?: number;
	getExamRegions?: boolean;
	examIdInSnap?: number;
	getRegionChange?: boolean;
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	onChange,
	value,
	functionarySrcmId,
	getExamRegions,
	examIdInSnap,
	getRegionChange,
}: RemoteSingleSelectProps) => {
	const { selectedRole, loggedInUserRoles } = useUserSession();
	const { showToastMessage } = useToastMessage();
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);
	const isAdmin = selectedRole === UserRole.SNP_ADMIN;
	const isExamAdmin = selectedRole === UserRole.EXAM_ADMIN;
	const isPreworkMentor = selectedRole === UserRole.PREWORK_MENTOR;
	const { t } = useTranslation();
	const { callApi } = useCallApi();

	const fetchRegionsFromZones = useCallback(
		async (zones: Zone[], searchValue: string) => {
			try {
				const parentIds = await Promise.all(
					zones.map(async (zone) => {
						try {
							const data = await fetchStaticGroupById(zone.zoneId);

							if (!data) {
								return null;
							}

							return data.parent_id;
						} catch (err) {
							const errorMsg = displayError(err);
							showToastMessage('error', errorMsg);
							return null;
						}
					})
				);

				const filteredParentIds = parentIds.filter(
					(id): id is number => id !== null
				);

				const uniqueParentIds = Array.from(new Set(filteredParentIds));

				const regions = await Promise.all(
					uniqueParentIds.map(async (id) => {
						try {
							return await fetchStaticGroupById(id);
						} catch (err) {
							const errorMsg = displayError(err);
							showToastMessage('error', errorMsg);
							return null;
						}
					})
				);

				const filteredRegions = regions.filter((region) => {
					if (!searchValue) return true;

					return region?.name
						?.toLowerCase()
						.includes(searchValue.toLowerCase());
				});

				return filteredRegions;
			} catch (err) {
				const errorMsg = displayError(err);
				showToastMessage('error', errorMsg);
				return [];
			}
		},
		[showToastMessage]
	);

	const fetchData = useCallback(
		async (searchValue: string) => {
			setLoading(true);

			if (functionarySrcmId && (isAdmin || isExamAdmin || isPreworkMentor)) {
				const regionsPage = await getRegionsProfile();
				let filtered = regionsPage.results;

				if (searchValue) {
					filtered = filtered.filter((item) =>
						item.name?.toLowerCase().includes(searchValue.toLowerCase())
					);
				}

				const transformedOptions: OptionData[] = filtered.map((item) => ({
					value: item.id,
					label: item.name,
				}));

				setOptions(transformedOptions);
			} else if (getExamRegions) {
				try {
					if (!functionarySrcmId || !examIdInSnap) {
						setOptions([]);
						return;
					}

					const examRegions = await callApi(
						() =>
							getRegionsOfExaminationCandidates({
								functionarySrcmId,
								examId: examIdInSnap,
							}),
						'errorOccurredWhileFetchingRegions'
					);

					const regions: Region[] = examRegions?.regions ?? [];

					const normalized = regions
						.filter(
							(r) =>
								typeof r.regionId === 'number' || typeof r.regionId === 'string'
						)
						.filter((r) => typeof r.regionName === 'string')
						.map((r) => ({
							regionId:
								typeof r.regionId === 'string'
									? Number(r.regionId)
									: r.regionId,
							regionName: r.regionName,
						}))
						.filter((r) => Number.isFinite(r.regionId));

					const transformedOptions: OptionData[] = normalized
						.filter((r) =>
							r.regionName.toLowerCase().includes(searchValue.toLowerCase())
						)
						.map((r) => ({
							value: r.regionId,
							label: r.regionName,
						}));

					setOptions(transformedOptions);
				} catch {
					setOptions([]);
				} finally {
					setLoading(false);
				}
				return;
			} else if (getRegionChange) {
				const currentRoleDetails = loggedInUserRoles?.find(
					(item) => item?.role?.code == selectedRole
				);
				const userZones = currentRoleDetails?.zones ?? [];

				const normalized = userZones
					.filter(
						(z) =>
							(typeof z.zoneId === 'number' || typeof z.zoneId === 'string') &&
							typeof z.zoneName === 'string'
					)
					.map((z) => ({
						zoneId: typeof z.zoneId === 'string' ? Number(z.zoneId) : z.zoneId,
						zoneName: z.zoneName,
					}))
					.filter((z) => Number.isFinite(z.zoneId));

				const regions = await fetchRegionsFromZones(normalized, searchValue);

				const option = regions.map((r) => ({
					value: r?.id,
					label: r?.name,
				}));

				setOptions(option);
				return;
			} else {
				setOptions([]);
			}
			setLoading(false);
		},
		[
			functionarySrcmId,
			isAdmin,
			isPreworkMentor,
			callApi,
			examIdInSnap,
			getExamRegions,
			getRegionChange,
			loggedInUserRoles,
			fetchRegionsFromZones,
			selectedRole,
			isExamAdmin,
		]
	);

	const debouncedFetchData = useMemo(
		() => debounce(fetchData, 500),
		[fetchData]
	);

	const handleSearch = useCallback(
		(searchKey: SelectValue) => {
			void debouncedFetchData(searchKey as string);
		},
		[debouncedFetchData]
	);

	const handleFocus = () => {
		if (options.length === 0) {
			void fetchData('');
		}
	};

	return (
		<Select
			showSearch
			defaultActiveFirstOption={false}
			filterOption={false}
			placeholder={t('searchRegions')}
			notFoundContent={loading ? <Spin size="small" /> : null}
			onFocus={handleFocus}
			onSearch={handleSearch}
			onChange={onChange}
			labelInValue
			value={value}
			suffixIcon={null}
			className="font-sans text-sm text-secondary-gray"
		>
			{options.map((option) => (
				<Option key={option.value} value={option.value}>
					{option.label}
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
